import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ErrorHandlingService } from '../../core/services/error-handling.service';
import {
  CaseBehavior,
  FidelityCheck,
  FidelityCheckModel,
  FidelityCheckQueryParams,
  FidelityReviewResult,
  FidelityReviewSection,
  ReviewScoreType,
  sendEmailPayload,
} from './fidelity.interface';
import {
  ApiListResponse,
  ApiResponse,
} from '../../core/models/api-response.models';

@Injectable({
  providedIn: 'root',
})
export class FidelityCheckService {
  private readonly API_URL = `${environment.apiUrl}/fidelity`;
  private readonly http = inject(HttpClient);
  private readonly errorHandler = inject(ErrorHandlingService);
  private readonly fidelityCheckSubject =
    new BehaviorSubject<ApiListResponse<FidelityCheck> | null>(null);
  fidelityChecks$ = this.fidelityCheckSubject.asObservable();

  private buildQueryParams(queryParams: FidelityCheckQueryParams): HttpParams {
    let params = new HttpParams();

    params = params.set('page', queryParams.page.toString());
    params = params.set('pageSize', queryParams.pageSize.toString());

    if (queryParams.sortOrder) {
      params = params.set('sortOrder', queryParams.sortOrder);
    }

    if (queryParams.sort) {
      params = params.set('sort', queryParams.sort);
    }

    if (queryParams.searchTerm) {
      params = params.set('searchTerm', queryParams.searchTerm.toString());
    }

    if (queryParams.studentId) {
      params = params.set('studentId', queryParams.studentId.toString());
    }

    if (queryParams.studentName) {
      params = params.set('studentName', queryParams.studentName);
    }

    if (queryParams.caseOwnerName) {
      params = params.set('caseOwnerName', queryParams.caseOwnerName);
    }

    if (queryParams.organizationId) {
      params = params.set(
        'organizationId',
        queryParams.organizationId.toString()
      );
    }

    if (queryParams.behaviorStatusId) {
      params = params.set(
        'behaviorStatusId',
        queryParams.behaviorStatusId.toString()
      );
    }

    if (queryParams.assessmentStatusId) {
      params = params.set(
        'assessmentStatusId',
        queryParams.assessmentStatusId.toString()
      );
    }

    if (queryParams.strategyStatusId) {
      params = params.set(
        'strategyStatusId',
        queryParams.strategyStatusId.toString()
      );
    }

    if (queryParams.dataEnteredStatusId) {
      params = params.set(
        'dataEnteredStatusId',
        queryParams.dataEnteredStatusId.toString()
      );
    }

    return params;
  }

  getFidelityList(
    queryParams: FidelityCheckQueryParams
  ): Observable<ApiListResponse<FidelityCheck>> {
    const params = this.buildQueryParams(queryParams);
    return this.http
      .get<ApiListResponse<FidelityCheck>>(`${this.API_URL}/list`, {
        params: params,
      })
      .pipe(
        tap(response => this.fidelityCheckSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getFidelityBybehvaiorIdandType(behaviorId: number, reviewType?: string) {
    let url = `${this.API_URL}/get-fidelity-by-case-behaviour-id?caseBehaviourId=${behaviorId}`;
    if (reviewType) {
      url += `&reviewType=${reviewType}`;
    }
    return this.http
      .get<ApiResponse<FidelityReviewResult>>(url)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getFidelityByIdandType(id: number, reviewType: number) {
    return this.http
      .get<
        ApiResponse<FidelityReviewSection>
      >(`${this.API_URL}/get-fidelity-by-id?id=${id}&reviewType=${reviewType}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  createFidelityCheck(
    payload: FidelityCheckModel
  ): Observable<ApiResponse<void>> {
    return this.http
      .post<ApiResponse<void>>(`${this.API_URL}/create`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  updateFidelityCheck(
    payload: FidelityCheckModel,
    id: number
  ): Observable<ApiResponse<void>> {
    return this.http
      .put<
        ApiResponse<void>
      >(`${this.API_URL}/update?fidelityCheckId=${id}`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getFidelitySettingByOrgId(orgId: number) {
    return this.http
      .get<
        ApiResponse<ReviewScoreType[]>
      >(`${this.API_URL}/get-fidelity-setting?orgnizationId=${orgId}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getFidelityBehvaiorList(caseId: number) {
    return this.http
      .get<
        ApiResponse<CaseBehavior[]>
      >(`${this.API_URL}/get-fidelity-by-case-behaviour-list?caseId=${caseId}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  sendEmailNotifications(
    payload: sendEmailPayload
  ): Observable<ApiResponse<void>> {
    return this.http
      .post<
        ApiResponse<void>
      >(`${this.API_URL}/send-email-notification`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  private readonly reviewScoreData = new BehaviorSubject<ApiResponse<
    ReviewScoreType[]
  > | null>(null);
  reviewScoreData$ = this.reviewScoreData.asObservable();

  getFidelitySettings(orgId: number) {
    return this.http
      .get<
        ApiResponse<ReviewScoreType[]>
      >(`${this.API_URL}/get-fidelity-setting?orgnizationId=${orgId}`)
      .pipe(
        tap((response: ApiResponse<ReviewScoreType[]>) =>
          this.reviewScoreData.next(response)
        ),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  createFidelitySetting(
    payload: ReviewScoreType
  ): Observable<ApiResponse<ReviewScoreType>> {
    return this.http
      .post<
        ApiResponse<ReviewScoreType>
      >(`${this.API_URL}/create-fidelity-setting`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  updateFidelitySetting(
    payload: ReviewScoreType
  ): Observable<ApiResponse<ReviewScoreType>> {
    return this.http
      .put<
        ApiResponse<ReviewScoreType>
      >(`${this.API_URL}/update-fidelity-setting?fidelitySettingId=${payload.id}`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  deleteFidelitySetting(reviewScoreTypeId: number) {
    return this.http
      .delete<
        ApiResponse<ReviewScoreType>
      >(`${this.API_URL}/${reviewScoreTypeId}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }
}
